import { Link } from "react-router-dom";
import { Target, Eye, BookOpen, ArrowRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { CtaBanner } from "../components/CtaBanner";
import { Placeholder } from "../components/Placeholder";
import { site, images, galleryImages, btnSecondary } from "../config/siteConfig";

const philosophy = [
  {
    step: "01",
    title: "The Right Environment",
    text: "Children learn best where they feel safe, welcomed and encouraged. Our classrooms are built around that belief.",
  },
  {
    step: "02",
    title: "Confidence Through Learning",
    text: "Every lesson is an opportunity for a student to ask questions, try again and grow in confidence.",
  },
  {
    step: "03",
    title: "A Strong Foundation",
    text: "We focus on strong basics — reading, writing, numeracy and character — so students are ready for every next step.",
  },
];

export default function AboutPage() {
  return (
    <>
      <Seo
        title="About Ansar Public School | School in Mundeke Goraya, Daska"
        description="Learn about Ansar Public School in Mundeke Goraya, Daska — our environment, philosophy and commitment to local families."
      />
      <PageHero
        chapter="01"
        eyebrow="About Us"
        title="A Local School, Built on Trust"
        description="Ansar Public School is a local educational institution serving families in Mundeke Goraya and the surrounding Daska and Sialkot communities."
        crumb="About"
      />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <Reveal>
              <SectionHeading chapter="02" eyebrow="Who We Are" title="Rooted in the Community We Serve" />
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 text-base leading-relaxed text-brand-muted sm:text-lg">
                Every student's educational journey begins with the right environment.
                Ansar Public School is committed to providing a welcoming space where
                students can learn, develop confidence and build a strong foundation for
                the future.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-5">
                <Placeholder>{site.schoolHistory}</Placeholder>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="spotlight-frame rounded-[2rem]">
              <img
                src={images.teacherStudent}
                alt="Teacher guiding a student during a lesson"
                loading="lazy"
                width="800"
                height="600"
                className="aspect-[4/3] w-full rounded-[2rem] object-cover shadow-xl"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-brand-line bg-white">
        <div className="mx-auto grid max-w-7xl gap-5 px-4 py-16 sm:px-6 md:grid-cols-2 lg:py-20">
          <Reveal className="h-full">
            <article data-testid="mission-card" className="flex h-full flex-col gap-4 rounded-3xl border border-brand-line bg-brand-cream p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand">
                <Target className="h-6 w-6 text-white" aria-hidden="true" />
              </span>
              <h2 className="font-display text-2xl font-bold text-brand-ink">Our Mission</h2>
              <Placeholder>{site.mission}</Placeholder>
              <p className="text-sm text-brand-muted">
                The school's official mission statement will be published here once provided.
              </p>
            </article>
          </Reveal>
          <Reveal delay={0.1} className="h-full">
            <article data-testid="vision-card" className="flex h-full flex-col gap-4 rounded-3xl border border-brand-line bg-brand-cream p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-green">
                <Eye className="h-6 w-6 text-white" aria-hidden="true" />
              </span>
              <h2 className="font-display text-2xl font-bold text-brand-ink">Our Vision</h2>
              <Placeholder>{site.vision}</Placeholder>
              <p className="text-sm text-brand-muted">
                The school's official vision statement will be published here once provided.
              </p>
            </article>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
        <Reveal>
          <SectionHeading
            chapter="03"
            eyebrow="Educational Philosophy"
            title="How We Think About Learning"
            align="center"
          />
        </Reveal>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {philosophy.map((p, i) => (
            <Reveal key={p.step} delay={i * 0.08} className="h-full">
              <article
                data-testid={`philosophy-card-${i}`}
                className="flex h-full flex-col gap-3 rounded-3xl border border-brand-line bg-white p-7 shadow-sm transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <span className="font-display text-sm font-extrabold text-brand-amber">{p.step}</span>
                <h3 className="font-display text-xl font-bold text-brand-ink">{p.title}</h3>
                <p className="text-sm leading-relaxed text-brand-muted">{p.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-brand-line bg-white">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24">
          <Reveal>
            <div className="spotlight-frame rounded-[2rem]">
              <img
                src={images.environment}
                alt="Bright, well-kept classroom ready for students"
                loading="lazy"
                width="800"
                height="600"
                className="aspect-[4/3] w-full rounded-[2rem] object-cover shadow-xl"
              />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <SectionHeading chapter="04" eyebrow="Learning Environment" title="A Welcoming Space to Learn" />
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 text-base leading-relaxed text-brand-muted sm:text-lg">
                Families are welcome to visit the campus in Mundeke Goraya and see the
                learning environment first-hand. Facility details will be listed here once
                verified by the school.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-5 flex flex-wrap items-center gap-3">
                <Placeholder>{site.facilities}</Placeholder>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
        <Reveal>
          <article
            data-testid="leadership-card"
            className="flex flex-col items-start gap-5 rounded-[2.5rem] border border-brand-line bg-brand-cream p-8 sm:p-12"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand">
              <BookOpen className="h-6 w-6 text-white" aria-hidden="true" />
            </span>
            <h2 className="font-display text-2xl font-bold text-brand-ink sm:text-3xl">
              School Leadership
            </h2>
            <Placeholder>{site.leadership}</Placeholder>
            <p className="max-w-2xl text-sm leading-relaxed text-brand-muted">
              A message from the school's leadership will be published here once provided.
              We do not publish names or titles without the school's confirmation.
            </p>
          </article>
        </Reveal>
      </section>

      <section className="border-t border-brand-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20">
          <Reveal>
            <SectionHeading chapter="05" eyebrow="Campus" title="A Glimpse of School Life" align="center" />
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3">
            {galleryImages.slice(6, 9).map((img, i) => (
              <Reveal key={img.src} delay={i * 0.06}>
                <div className="overflow-hidden rounded-2xl border border-brand-line">
                  <img
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                    width="800"
                    height="600"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8 text-center">
            <Link to="/gallery" data-testid="about-gallery-link" className={btnSecondary}>
              View Full Gallery
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20">
        <CtaBanner
          idPrefix="about-admissions"
          title="Ready to Explore Admission?"
          text="Speak with our school team to learn about available classes, admission requirements, timings and the next steps."
          primaryLabel="Start an Admission Enquiry"
          primaryTo="/admissions"
          secondaryLabel="Call the School"
          secondaryHref={site.phoneHref}
        />
      </section>
    </>
  );
}
